import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ImageOff, LayoutGrid, Plus, Sparkles, Store, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import { AI_IMAGE_POLICIES, isUnlimited, type AiImagePolicy } from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands, useCreateBrand, useCurrentWorkspace, useUpdateBrand } from "@/lib/queries";
import { useOnboarding } from "@/lib/onboarding";
import { Badge, Card, Notice, PageHeader, SkeletonRows, Spinner } from "@/components/ui";
import { ErrorBox, Field } from "./LoginPage";

const POLICY_ICONS: Record<AiImagePolicy, typeof Sparkles> = {
  generate: Sparkles,
  enhance: Wand2,
  off: ImageOff,
};

export function BrandsPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const workspace = useCurrentWorkspace();
  const createBrand = useCreateBrand();
  const onboarding = useOnboarding();

  const used = brands.data?.length ?? 0;
  const limit = workspace.data?.limits.maxBrands ?? 1;
  const atLimit = used >= limit;
  const isFirstBrand = !brands.isLoading && used === 0;

  // The very first thing a new account must do, so the form is already open
  // rather than hidden behind a button they have to find.
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [error, setError] = useState<string | null>(null);

  const showForm = open || isFirstBrand;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await createBrand.mutateAsync({ name, industry: industry.trim() || null });
      setName("");
      setIndustry("");
      setOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<LayoutGrid size={18} />}
        title={t("brands.title")}
        subtitle={t("brands.subtitle")}
        actions={
          <>
            {workspace.data ? (
              <span className="hidden text-xs tabular-nums text-muted sm:inline">
                {t("workspace.brandsUsed", {
                  used,
                  limit: isUnlimited(limit) ? t("common.unlimited") : limit,
                })}
              </span>
            ) : null}
            {!isFirstBrand ? (
              <button
                onClick={() => setOpen((v) => !v)}
                disabled={atLimit}
                title={atLimit ? t("brands.limitReached") : undefined}
                className="btn-primary btn-sm"
              >
                <Plus size={15} /> {t("brands.add")}
              </button>
            ) : null}
          </>
        }
      />

      {/* The one moment where a full-width prompt is warranted: an empty
          account can do nothing else until this exists. */}
      {isFirstBrand ? (
        <div className="overflow-hidden rounded-2xl bg-night-900 px-6 py-7 text-white sm:px-8">
          <Badge tone="info">{t("onboarding.stepOf", { n: 1, total: onboarding.total })}</Badge>
          <h2 className="mt-3 text-xl font-semibold tracking-tight">{t("brands.firstTitle")}</h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
            {t("brands.firstBody")}
          </p>
        </div>
      ) : null}

      {atLimit && !isFirstBrand ? <Notice tone="warning">{t("brands.limitReached")}</Notice> : null}

      {showForm ? (
        <Card>
          <form onSubmit={submit} className="max-w-md space-y-4">
            <Field label={t("brands.name")} value={name} onChange={setName} required autoFocus />
            <Field
              label={t("brands.industry")}
              value={industry}
              onChange={setIndustry}
              hint={t("brands.industryHint")}
            />
            {error ? <ErrorBox>{error}</ErrorBox> : null}
            <div className="flex items-center gap-2">
              <button type="submit" disabled={createBrand.isPending} className="btn-primary">
                {createBrand.isPending ? <Spinner /> : <Plus size={15} />}
                {createBrand.isPending ? t("brands.creating") : t("brands.create")}
              </button>
              {!isFirstBrand ? (
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">
                  {t("common.cancel")}
                </button>
              ) : null}
            </div>
          </form>
        </Card>
      ) : null}

      {brands.isLoading ? (
        <Card>
          <SkeletonRows rows={2} />
        </Card>
      ) : used === 0 ? null : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {brands.data!.map((b) => (
            <li key={b.id} className="min-w-0">
              <BrandCard brand={b} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BrandCard({ brand }: { brand: NonNullable<ReturnType<typeof useBrands>["data"]>[number] }) {
  const { t } = useTranslation();
  const update = useUpdateBrand();
  const [error, setError] = useState<string | null>(null);

  const pages = brand._count?.socialAccounts ?? 0;
  const posts = brand._count?.posts ?? 0;
  const policy = (brand.aiImagePolicy ?? "generate") as AiImagePolicy;
  const PolicyIcon = POLICY_ICONS[policy];

  async function setPolicy(next: AiImagePolicy) {
    setError(null);
    try {
      await update.mutateAsync({ id: brand.id, input: { aiImagePolicy: next } });
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }

  return (
    <div className="card flex h-full flex-col p-5 transition-shadow hover:shadow-lift">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <Store size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink">{brand.name}</p>
          <p className="truncate text-xs text-muted">
            {brand.industry ? `${brand.industry} · ` : ""}
            {brand.country} · {brand.timezone}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Stat label={t("brands.pagesLabel")} value={pages} />
        <Stat label={t("brands.postsLabel")} value={posts} />
      </div>

      {/* ── AI images ───────────────────────────────────────────────────── */}
      <div className="mt-4 border-t border-border pt-4">
        <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink">
          <PolicyIcon size={14} className="text-primary-600" />
          {t("brands.aiImages.title")}
        </p>
        <div className="mt-2 grid gap-1.5">
          {AI_IMAGE_POLICIES.map((option) => (
            <label
              key={option}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors ${
                policy === option
                  ? "border-primary-300 bg-primary-50"
                  : "border-transparent hover:bg-surface"
              }`}
            >
              <input
                type="radio"
                name={`ai-${brand.id}`}
                checked={policy === option}
                onChange={() => void setPolicy(option)}
                disabled={update.isPending}
                className="mt-0.5"
              />
              <span>
                <span className="block font-medium text-ink">
                  {t(`brands.aiImages.${option}.label`)}
                </span>
                <span className="block leading-relaxed text-muted">
                  {t(`brands.aiImages.${option}.body`)}
                </span>
              </span>
            </label>
          ))}
        </div>
        {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      </div>

      <Link
        to={pages === 0 ? "/connect" : "/photo"}
        className="group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
      >
        {pages === 0 ? t("brands.connectCta") : t("brands.createCta")}
        <ArrowRight size={14} className="flip-rtl transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-surface px-3 py-2">
      <p className="text-lg font-semibold tabular-nums leading-none text-ink">{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wide text-muted">{label}</p>
    </div>
  );
}
